import { Controller } from "stimulus";

export default class extends Controller {
  declare modalTarget: HTMLElement;
  declare hasModalTarget: boolean;
  declare hasContainerTarget: boolean;
  declare containerTarget: HTMLElement;
  declare hasFrameTarget: boolean;
  declare frameTarget: HTMLElement;
  static targets = ["modal", "container", "frame"];

  declare defaultOpenedValue: boolean;
  declare shouldDismissOnClickOutsideValue: boolean;
  static values = {
    defaultOpened: {
      type: Boolean,
      default: false
    },
    shouldDismissOnClickOutside: {
      type: Boolean,
      default: false
    }
  };

  private originalPosition = "initial";
  private originalZIndex = "initial";
  onOpenCallbacks: (() => void)[] = [];
  onCloseCallbacks: (() => void)[] = [];

  connect(): void {
    if (!this.hasModalTarget) {
      console.warn("Modal controller connected without a modal target");
      return
    }
    this.modalTarget.hidden = true;
    this.originalPosition = this.modalTarget.style.position ?? "initial";
    this.originalZIndex = this.modalTarget.style.zIndex ?? "initial";

    if (this.defaultOpenedValue) {
      this.showModal();
    }

    if (this.shouldDismissOnClickOutsideValue && this.hasContainerTarget) {
      this.dismissOnClickOutside = this.dismissOnClickOutside.bind(this);
      this.modalTarget.addEventListener("click", this.dismissOnClickOutside);
    }
  }

  disconnect(): void {
    if (!this.hasModalTarget) {
      return
    }
    this.modalTarget.removeEventListener("click", this.dismissOnClickOutside);
  }

  registerCallbacks(opts: { onOpen: () => void; onClose: () => void }) {
    this.onOpenCallbacks.push(opts.onOpen);
    this.onCloseCallbacks.push(opts.onClose);
  }

  showModal() {
    if (!this.hasModalTarget) {
      return
    }

    // If there's a frame target, show a loading spinner while content is being fetched
    if (this.hasFrameTarget) {
      this.frameTarget.innerHTML = `
        <div class="flex items-center justify-center py-12">
          <div class="flex flex-col items-center gap-3">
            <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      `;
    }

    this.modalTarget.hidden = false;
    this.modalTarget.style.position = "fixed";
    this.modalTarget.style.zIndex = "50";
    this.onOpenCallbacks.forEach((cb) => cb());
  }

  dismissModal() {
    if (!this.hasModalTarget) {
      return
    }

    this.modalTarget.hidden = true;
    this.modalTarget.style.position = this.originalPosition;
    this.modalTarget.style.zIndex = this.originalZIndex;
    this.onCloseCallbacks.forEach((cb) => cb());
  }

  dismissOnClickOutside(event: MouseEvent) {
    if (this.modalTarget.hidden) {
      return
    }

    if (event.target instanceof window.Node
      && this.hasContainerTarget
      && !this.containerTarget.contains(event.target)) {
      this.dismissModal();
    }
  }
}